<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { AbuseCase } from '@/types/compliance'
import type { PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AbuseList' })

const router = useRouter()
const { t } = useI18n()

const loading = ref(true)
const abuseCases = ref<AbuseCase[]>([])
const statusFilter = ref<string>('all')
const categoryFilter = ref<string>('all')
const searchQuery = ref('')

// Pagination
const pagination = usePagination({ initialItemsPerPage: 50 })

// Status filter options
const statusOptions = [
  { value: 'all', title: 'Alle' },
  { value: 'new', title: 'Neu' },
  { value: 'investigating', title: 'In Prüfung' },
  { value: 'resolved', title: 'Gelöst' },
  { value: 'dismissed', title: 'Abgewiesen' }
]

// Category filter options
const categoryOptions = [
  { value: 'all', title: 'Alle Kategorien' },
  { value: 'spam', title: 'Spam' },
  { value: 'phishing', title: 'Phishing' },
  { value: 'malware', title: 'Malware' },
  { value: 'fake_data', title: 'Falsche Daten' },
  { value: 'trademark', title: 'Markenrecht' },
  { value: 'other', title: 'Sonstiges' }
]

// Fetch abuse cases
async function fetchAbuseCases() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()

  try {
    const res = await useAxios<null, PaginatedResponse<AbuseCase>>(
      'GET',
      `/admin/abuse?limit=${limit}&offset=${offset}`,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      abuseCases.value = res.data.data
      pagination.setTotal(res.data.total)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AbuseList' })
  } finally {
    loading.value = false
  }
}

// Watch for page changes
watch(() => pagination.page.value, () => {
  fetchAbuseCases()
})

// Pagination display helpers
const showingFrom = computed(() => ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1)
const showingTo = computed(() => Math.min(showingFrom.value + abuseCases.value.length - 1, pagination.total.value))

// Filtered cases
const filteredCases = computed(() => {
  let result = abuseCases.value

  if (statusFilter.value !== 'all') {
    result = result.filter(c => c.status === statusFilter.value)
  }

  if (categoryFilter.value !== 'all') {
    result = result.filter(c => c.category === categoryFilter.value)
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(c =>
      c.domain_name?.toLowerCase().includes(query) ||
      c.reported_ip?.toLowerCase().includes(query) ||
      c.vserver_name?.toLowerCase().includes(query) ||
      c.reporter_email?.toLowerCase().includes(query)
    )
  }

  return result
})

// Format date
function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

// Get status badge class
function getStatusClass(status: string): string {
  switch (status) {
    case 'new': return 'status-new'
    case 'investigating': return 'status-investigating'
    case 'resolved': return 'status-resolved'
    case 'dismissed': return 'status-dismissed'
    default: return ''
  }
}

// Get category badge class
function getCategoryClass(category: string): string {
  switch (category) {
    case 'spam': return 'category-spam'
    case 'phishing': return 'category-phishing'
    case 'malware': return 'category-malware'
    case 'fake_data': return 'category-fake'
    case 'trademark': return 'category-trademark'
    default: return 'category-other'
  }
}

// Get category label
function getCategoryLabel(category: string): string {
  const opt = categoryOptions.find(c => c.value === category)
  return opt?.title || category
}

function navigateToDetail(id: number) {
  router.push({ name: 'dashboardAdminAbuseDetail', params: { id } })
}

onMounted(() => {
  fetchAbuseCases()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.abuse.title')"
    :subtitle="t('dashboard.admin.compliance.abuse.subtitle')"
    icon="mdi-alert-circle-outline"
    icon-color="red"
    show-back
    :back-route="{ name: 'dashboardAdminCompliance' }"
  >
    <template #actions>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchAbuseCases"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Filters -->
    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="t('dashboard.admin.compliance.abuse.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <v-select
        v-model="categoryFilter"
        :items="categoryOptions"
        item-title="title"
        item-value="value"
        variant="outlined"
        density="compact"
        hide-details
        class="category-select"
      />
      <v-btn-toggle
        v-model="statusFilter"
        mandatory
        density="compact"
        class="status-toggle"
      >
        <v-btn
          v-for="opt in statusOptions"
          :key="opt.value"
          :value="opt.value"
          variant="outlined"
          size="small"
        >
          {{ opt.title }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredCases.length === 0" class="empty-state">
      <v-icon icon="mdi-check-circle-outline" size="64" />
      <h3>{{ t('dashboard.admin.compliance.abuse.empty') }}</h3>
      <p>{{ t('dashboard.admin.compliance.abuse.emptyDescription') }}</p>
    </div>

    <!-- Abuse Cases Table -->
    <div v-else class="table-container">
      <table class="abuse-table">
        <thead>
          <tr>
            <th>{{ t('dashboard.admin.compliance.abuse.columns.date') }}</th>
            <th>{{ t('dashboard.admin.compliance.abuse.columns.domain') }}</th>
            <th>{{ t('dashboard.admin.compliance.abuse.columns.category') }}</th>
            <th>{{ t('dashboard.admin.compliance.abuse.columns.reporter') }}</th>
            <th>{{ t('dashboard.admin.compliance.abuse.columns.status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="abuseCase in filteredCases"
            :key="abuseCase.id"
            class="table-row"
            @click="navigateToDetail(abuseCase.id)"
          >
            <td class="date-cell">{{ formatDate(abuseCase.created) }}</td>
            <td class="domain-cell">
              <span class="domain-name">{{ abuseCase.domain_name || abuseCase.reported_ip }}</span>
              <span v-if="abuseCase.vserver_id" class="vserver-target">
                {{ abuseCase.vserver_name || `#${abuseCase.vserver_id}` }}
              </span>
            </td>
            <td class="category-cell">
              <span class="category-badge" :class="getCategoryClass(abuseCase.category)">
                {{ getCategoryLabel(abuseCase.category) }}
              </span>
            </td>
            <td class="reporter-cell">
              {{ abuseCase.reporter_email || '-' }}
            </td>
            <td class="status-cell">
              <span class="status-badge" :class="getStatusClass(abuseCase.status)">
                {{ abuseCase.status }}
              </span>
            </td>
            <td class="action-cell">
              <v-btn icon variant="text" size="small">
                <v-icon icon="mdi-chevron-right" />
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Controls -->
    <div v-if="pagination.total.value > 0 && !loading" class="pagination-controls">
      <div class="pagination-info">
        {{ t('dashboard.admin.compliance.abuse.pagination.showing', { from: showingFrom, to: showingTo, total: pagination.total.value }) }}
      </div>
      <div class="pagination-buttons">
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.goToPage(1)"
        >
          <v-icon icon="mdi-chevron-double-left" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === 1"
          @click="pagination.prevPage()"
        >
          <v-icon icon="mdi-chevron-left" size="18" />
        </button>
        <span class="page-indicator">
          {{ pagination.page.value }} / {{ pagination.totalPages.value }}
        </span>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.nextPage()"
        >
          <v-icon icon="mdi-chevron-right" size="18" />
        </button>
        <button
          class="pagination-btn"
          :disabled="pagination.page.value === pagination.totalPages.value"
          @click="pagination.goToPage(pagination.totalPages.value)"
        >
          <v-icon icon="mdi-chevron-double-right" size="18" />
        </button>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Filters */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-input {
  max-width: 280px;
  flex: 1;
}

.category-select {
  max-width: 200px;
}

.status-toggle {
  flex-wrap: wrap;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(76, 175, 80, 0.4);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0;
  }
}

/* Table */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.abuse-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  white-space: nowrap;
}

.domain-cell .vserver-target {
  display: block;
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.55);
}

.domain-cell .domain-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.reporter-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Category Badges */
.category-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.category-spam {
    background: rgba(158, 158, 158, 0.15);
    color: #bdbdbd;
  }

  &.category-phishing {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.category-malware {
    background: rgba(156, 39, 176, 0.15);
    color: #ce93d8;
  }

  &.category-fake {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.category-trademark {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.category-other {
    background: rgba(120, 163, 255, 0.15);
    color: #90caf9;
  }
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;

  &.status-new {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-investigating {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-resolved {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.status-dismissed {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
  }
}

.action-cell {
  width: 48px;
  text-align: right;
}

/* Light Theme */
.v-theme--lightTheme {
  .table-container {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .abuse-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .domain-cell .domain-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .date-cell,
  .reporter-cell {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .abuse-list {
    padding: 16px;
  }

  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input,
  .category-select {
    max-width: 100%;
  }

  .table-container {
    overflow-x: auto;
  }

  .abuse-table {
    min-width: 650px;
  }
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-top: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 80px;
  text-align: center;
}
</style>
